import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getCourseQuizQuestions, submitCourseQuiz, requestAssessment, GetCourseQuizQuestionsOutputType, SubmitCourseQuizOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, ChevronRight, ShieldAlert, Award, Clock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

type Question = GetCourseQuizQuestionsOutputType['questions'][0];

function QuizOption({ label, text, selected, correct, revealed, onClick, disabled }: {
  label: string; text: string; selected: boolean; correct?: boolean; revealed: boolean; onClick: () => void; disabled?: boolean;
}) {
  let cls = 'border-border bg-card';
  if (revealed && correct) cls = 'border-primary bg-primary/10';
  else if (revealed && selected && !correct) cls = 'border-destructive bg-destructive/10';
  else if (selected) cls = 'border-primary bg-primary/5';

  return (
    <button onClick={onClick} disabled={revealed || disabled}
      className={`w-full text-left p-3.5 rounded-lg border-2 transition-all flex items-start gap-3 ${cls} active:scale-[0.98] touch-manipulation`}>
      <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 ${
        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40 text-muted-foreground'
      }`}>{label}</span>
      <span className="text-sm text-foreground leading-relaxed pt-1">{text}</span>
    </button>
  );
}

function QuestionView({ q, answer, onSelect, index, total }: {
  q: Question; answer: string; onSelect: (ans: string) => void; index: number; total: number;
}) {
  // Determine question type for rendering
  const options = [
    { key: 'A', text: q.optionA },
    { key: 'B', text: q.optionB },
    { key: 'C', text: q.optionC },
    { key: 'D', text: q.optionD },
  ].filter(o => o.text);

  const isTrueFalse = options.length === 2 &&
    ((options[0].text.toLowerCase() === 'true' && options[1].text.toLowerCase() === 'false') ||
     (options[0].text.toLowerCase() === 'yes' && options[1].text.toLowerCase() === 'no'));

  return (
    <div className="px-4 pt-5 pb-3">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs font-bold text-muted-foreground">Q{index + 1} of {total}</span>
        {q.isCritical && (
          <span className="text-xs bg-destructive/15 text-destructive px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Critical Safety
          </span>
        )}
        {q.questionType === 'Scenario' && (
          <span className="text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded-full">Scenario</span>
        )}
        {isTrueFalse && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">True / False</span>
        )}
      </div>

      {q.imageUrl && (
        <img src={q.imageUrl} alt="" className="w-full rounded-lg mb-4 max-h-52 object-cover border border-border" />
      )}

      <p className="text-base font-medium text-foreground leading-relaxed mb-5">{q.question}</p>

      {isTrueFalse ? (
        <div className="grid grid-cols-2 gap-3">
          {options.map(opt => (
            <button key={opt.key} onClick={() => onSelect(opt.key)}
              className={`p-4 rounded-lg border-2 text-center font-semibold text-sm transition-all active:scale-95 touch-manipulation ${
                answer === opt.key ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-foreground'
              }`}>
              {opt.text}
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {options.map(opt => (
            <QuizOption
              key={opt.key}
              label={opt.key}
              text={opt.text}
              selected={answer === opt.key}
              revealed={false}
              onClick={() => onSelect(opt.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ResultScreen({ result, onRetake, onBack, onRequestAssessment, enrollmentId }: {
  result: SubmitCourseQuizOutputType;
  onRetake: () => void;
  onBack: () => void;
  onRequestAssessment: () => void;
  enrollmentId: string;
}) {
  const [requesting, setRequesting] = useState(false);
  const [assessmentMsg, setAssessmentMsg] = useState('');

  const handleRequest = async () => {
    if (requesting) return;
    setRequesting(true);
    onRequestAssessment();
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Result banner */}
      <div className={`text-center py-8 rounded-2xl border-2 ${result.passed ? 'bg-primary/5 border-primary/30' : 'bg-destructive/5 border-destructive/30'}`}>
        {result.passed ? (
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-3" />
        ) : (
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-3" />
        )}
        <h1 className="text-2xl font-bold text-foreground mb-1">{result.passed ? 'Quiz Passed!' : 'Not Quite'}</h1>
        <p className="text-5xl font-bold text-foreground mb-2">{result.score}%</p>
        <p className="text-sm text-muted-foreground">
          {result.correctCount} of {result.totalQuestions} correct · Pass mark: {result.passMark}%
        </p>

        {result.criticalFailed && (
          <div className="mt-4 mx-6 bg-destructive/10 rounded-lg p-3 flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs text-destructive text-left leading-relaxed">
              {result.criticalFailedQuestions.length} critical safety question(s) answered incorrectly. All critical questions must be correct to pass.
            </p>
          </div>
        )}
      </div>

      {/* Question-by-question feedback */}
      <div className="mt-5 bg-card border border-border rounded-lg divide-y divide-border">
        {result.feedback.map((f, i) => (
          <div key={f.questionId} className="flex items-center gap-3 px-3 py-2.5">
            {f.correct ? (
              <CheckCircle className="w-4 h-4 text-primary shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-destructive shrink-0" />
            )}
            <span className="text-sm text-foreground flex-1">Question {i + 1}</span>
            {f.isCritical && <ShieldAlert className="w-3.5 h-3.5 text-destructive shrink-0" />}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-6 space-y-3">
        {result.passed && (
          <Button onClick={onRequestAssessment}
            className="w-full bg-primary text-primary-foreground py-3.5 text-base h-auto touch-manipulation">
            <Award className="w-5 h-5 mr-2" />
            Request Practical Assessment
          </Button>
        )}
        {result.canRetake && (
          <Button variant="outline" onClick={onRetake}
            className="w-full py-3 h-auto touch-manipulation">
            <RotateCcw className="w-4 h-4 mr-2" /> Retake Quiz
          </Button>
        )}
        <Button variant="outline" onClick={onBack} className="w-full py-3 h-auto touch-manipulation">
          Back to Course
        </Button>
      </div>
    </div>
  );
}

export default function CourseModuleQuiz() {
  const { enrollmentId, moduleId } = useParams<{ enrollmentId: string; moduleId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [passMark, setPassMark] = useState(80);
  const [moduleLabel, setModuleLabel] = useState('');
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitCourseQuizOutputType | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [assessmentMessage, setAssessmentMessage] = useState('');
  const startedAtRef = useRef(new Date().toISOString());
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!user || !enrollmentId || !moduleId) return;
    getCourseQuizQuestions({ enrollmentId, moduleId })
      .then(d => {
        setQuestions(d.questions);
        setPassMark(d.passMark);
        setModuleLabel(d.moduleLabel);
        setAttemptNumber(d.attemptNumber);
        setMaxAttempts(d.maxAttempts);
      })
      .catch(e => setLoadError(e?.message || 'Could not load quiz'))
      .finally(() => setLoading(false));
  }, [user, enrollmentId, moduleId]);

  const selectAnswer = useCallback((qId: string, ans: string) => {
    setAnswers(prev => ({ ...prev, [qId]: ans }));
  }, []);

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setShowConfirm(false);
    const res = await submitCourseQuiz({
      enrollmentId: enrollmentId!,
      moduleId: moduleId!,
      answers: Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer })),
      startedAt: startedAtRef.current,
    }).catch(e => { toast.error(e?.message || 'Submission failed'); submittedRef.current = false; return null; });
    setSubmitting(false);
    if (res) setResult(res);
  };

  const handleRequestAssessment = async () => {
    if (assessmentMessage) return;
    const res = await requestAssessment({ enrollmentId: enrollmentId!, moduleId: moduleId! })
      .catch(e => { toast.error(e?.message || 'Request failed'); return null; });
    if (res) {
      setAssessmentMessage(res.message);
      toast.success('Assessment requested');
    }
  };

  const handleRetake = () => {
    setResult(null);
    setCurrent(0);
    setAnswers({});
    submittedRef.current = false;
    startedAtRef.current = new Date().toISOString();
    setLoading(true);
    getCourseQuizQuestions({ enrollmentId: enrollmentId!, moduleId: moduleId! })
      .then(d => {
        setQuestions(d.questions);
        setAttemptNumber(d.attemptNumber);
        setMaxAttempts(d.maxAttempts);
      })
      .catch(e => setLoadError(e?.message || 'Could not load quiz'))
      .finally(() => setLoading(false));
  };

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-48" /><Skeleton className="h-24" /><Skeleton className="h-14" /><Skeleton className="h-14" />
    </div>
  );

  if (loadError) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-3" />
      <h2 className="font-semibold text-foreground mb-1">Cannot Start Quiz</h2>
      <p className="text-sm text-muted-foreground mb-4">{loadError}</p>
      <Link to={`/train/${enrollmentId}/module/${moduleId}`} className="text-sm text-primary hover:underline">← Back to Module</Link>
    </div>
  );

  // RESULT SCREEN
  if (result) {
    if (assessmentMessage) {
      return (
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Assessment Requested</h1>
          <p className="text-sm text-muted-foreground mb-6">{assessmentMessage}</p>
          <Button onClick={() => navigate(`/train/${enrollmentId}`)} className="w-full py-3 h-auto">
            Back to Course
          </Button>
        </div>
      );
    }

    return (
      <ResultScreen
        result={result}
        onRetake={handleRetake}
        onBack={() => navigate(`/train/${enrollmentId}`)}
        onRequestAssessment={handleRequestAssessment}
        enrollmentId={enrollmentId!}
      />
    );
  }

  // QUIZ SCREEN
  const q = questions[current];
  if (!q) return null;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-lg mx-auto pb-24">
      {/* Sticky header */}
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-40">
        <div className="flex items-center justify-between mb-1">
          <Link to={`/train/${enrollmentId}/module/${moduleId}`}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{moduleLabel}</span>
          </Link>
          <div className="text-right">
            <span className="text-xs text-muted-foreground">{answeredCount}/{questions.length} answered</span>
            {maxAttempts > 0 && (
              <span className="text-xs text-muted-foreground ml-2">· Attempt {attemptNumber}/{maxAttempts}</span>
            )}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex gap-0.5 mt-2">
          {questions.map((qu, i) => (
            <button key={qu.id} onClick={() => setCurrent(i)}
              className={`flex-1 h-2 rounded-full transition-colors ${
                answers[qu.id] ? 'bg-primary' : i === current ? 'bg-primary/40' : 'bg-muted'
              }`} />
          ))}
        </div>
      </div>

      {/* Quiz info bar */}
      <div className="px-4 py-2 bg-muted/30 border-b border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Pass mark: {passMark}%</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Take your time</span>
      </div>

      {/* Question */}
      <QuestionView
        q={q}
        answer={answers[q.id] || ''}
        onSelect={(ans) => selectAnswer(q.id, ans)}
        index={current}
        total={questions.length}
      />

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-5 w-full max-w-sm shadow-lg">
            <h3 className="font-semibold text-foreground mb-2">Submit Quiz?</h3>
            <p className="text-sm text-muted-foreground mb-1">
              You have answered {answeredCount} of {questions.length} questions.
            </p>
            {answeredCount < questions.length && (
              <p className="text-xs text-destructive mb-3">
                {questions.length - answeredCount} unanswered question(s) will be marked incorrect.
              </p>
            )}
            <p className="text-xs text-muted-foreground mb-4">
              Once submitted, you cannot change your answers.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1 h-11 touch-manipulation">
                Review
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}
                className="flex-1 bg-primary text-primary-foreground h-11 touch-manipulation">
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex gap-3 z-50 max-w-lg mx-auto">
        <Button variant="outline" onClick={() => setCurrent(Math.max(0, current - 1))}
          disabled={current === 0}
          className="flex-1 h-12 touch-manipulation">
          Previous
        </Button>
        {current < questions.length - 1 ? (
          <Button onClick={() => setCurrent(current + 1)}
            className="flex-1 bg-primary text-primary-foreground h-12 touch-manipulation">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={() => setShowConfirm(true)} disabled={submitting}
            className="flex-1 bg-primary text-primary-foreground h-12 touch-manipulation">
            Submit Quiz
          </Button>
        )}
      </div>
    </div>
  );
}
